import { Divider, Tooltip } from 'drift-ui';
import React, { FC, useRef, useState } from 'react';
import styles from './BaseFormStyles.scss';
import { UploadFiles } from '../../components/UploadFiles';
import InfoIcon from '../../assets/images/info.svg';
import { DO_APPLICATION_TYPE, useSectionFormHelpers } from 'client-experience-toolbox';
import { useTranslation } from 'react-i18next';
import ContinueButton from '../../components/ContinueButton';
import { useFormNetworkCalls } from 'utils/useFormNetworkCalls';
import { useSelector } from 'react-redux';
import { sendDOFirebaseEvent } from 'utils/utils';
import { RootState } from 'app/configureStore';

type UploadDescriptionProps = {
  text: string;
  tooltipText: string;
  optional?: boolean;
};

const UploadDescription: FC<UploadDescriptionProps> = ({ text, tooltipText, optional }) => {
  const { t } = useTranslation();
  return (
    <div>
      <p className={styles.uploadDescription}>
        {text}
        <Tooltip
          title={tooltipText}
          placement="top"
          enterTouchDelay={0}
          leaveTouchDelay={8000}
          slotProps={{
            tooltip: {
              className: styles.tooltip,
            },
          }}
        >
          <img
            src={InfoIcon}
            alt="Information Icon"
            width={24}
            height={24}
            tabIndex={0}
            className={styles.tooltipIcon}
          />
        </Tooltip>
      </p>
      {optional && <p className={styles.optional}>{t('optional')}</p>}
    </div>
  );
};

export const UploadDocuments = () => {
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const { patchFullApplication } = useFormNetworkCalls(DO_APPLICATION_TYPE.FACTORING);
  const application = useSelector((state: RootState) => state?.applicationReducer?.factoringApplication);
  const { fileValidatorGenerator } = useSectionFormHelpers({
    errorSetter: setErrors,
    updateApplication: () => {},
    application,
    fireBaseLogEvent: sendDOFirebaseEvent,
  });
  const [isPatching, setIsPatching] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation('common', { keyPrefix: 'upload_documents_section' });

  const fieldValidations = {
    driverLicenseFiles: fileValidatorGenerator({
      key: `applicationData.applicants[0].driversLicenseFiles`,
      errorText: t('file_upload_error'),
    }),
  };

  const handleContinueClick = async () => {
    const isValid = await checkFormValidation();
    if (isValid) {
      return;
    } else {
      const firstErrorInput = formRef.current?.querySelector('[id^="fileError-"]') as HTMLElement;
      if (firstErrorInput) {
        firstErrorInput.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
        firstErrorInput.focus({ preventScroll: true });
      }
    }
  };

  const checkFormValidation = async () => {
    const results = Object.values(fieldValidations).map((validator, index) => ({
      index,
      isValid: validator(),
    }));
    const firstInvalid = results.find(({ isValid }) => !isValid);

    if (firstInvalid) {
      return false;
    } else {
      await patchFullApplication(setIsPatching);
      return true;
    }
  };
  return (
    <>
      <div ref={formRef} className={styles.formFieldContainer}>
        <h2 className={styles.title}>{t('upload_the_following_documents')}</h2>
        <UploadDescription text={t('drivers_license_title')} tooltipText={t('drivers_license_tooltip')} />
        <UploadFiles
          error={errors['applicationData.applicants[0].driversLicenseFiles']}
          validator={fieldValidations.driverLicenseFiles}
          fileKey="applicationData.applicants[0].driversLicenseFiles"
          documentType="driversLicenseOrPassport"
          secondaryText
        />
        <Divider />
        <UploadDescription
          text={t('insurance_certificate_title')}
          tooltipText={t('insurance_certificate_tooltip')}
          optional
        />
        <UploadFiles
          fileKey="applicationData.company.insurance.insuranceCertificateFiles"
          documentType="insuranceCertificate"
        />
        <Divider />
        <UploadDescription
          text={t('operating_agreement_title')}
          tooltipText={t('operating_agreement_tooltip')}
          optional
        />
        <UploadFiles
          fileKey="applicationData.company.supportingDocuments.operatingAgreementFiles"
          documentType="operatingAgreement"
        />
        <Divider />
        <UploadDescription
          text={t('articles_of_incorporation_title')}
          tooltipText={t('articles_of_incorporation_tooltip')}
          optional
        />
        <UploadFiles
          fileKey="applicationData.company.supportingDocuments.articlesOfIncorporationFiles"
          documentType="articlesOfIncorporation"
        />
        <div className={styles.footer}>
          <ContinueButton isPatching={isPatching} handleContinueClick={handleContinueClick} />
        </div>
      </div>
    </>
  );
};
